import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { 
  fetchOrdersPaginated, 
  getOrderCount, 
  PaginatedOrdersResult, 
  OrderStatus,
  fetchOrderPageCursors,
  Order
} from '@/services/orderService';

interface UsePaginatedOrdersOptions {
  userId: string;
  status?: OrderStatus | 'all-orders';
  pageSize?: number;
  enabled?: boolean;
}

interface UsePaginatedOrdersResult {
  orders: Order[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  reset: () => void;
}

/**
 * Hook for paginated order fetching with server-side pagination
 * Optimized for cost-effective Firestore queries
 */
export const usePaginatedOrders = ({ 
  userId,
  status = 'all-orders', 
  pageSize = 10,
  enabled = true 
}: UsePaginatedOrdersOptions): UsePaginatedOrdersResult => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Load products for a specific page
  const loadPage = useCallback(async (page: number) => {
    if (!enabled || !userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      
      if (page === 1) {
        cursor = null;
      } else {
        // Check if we have the cursor for the previous page
        cursor = pageCursorsRef.current.get(page - 1) || null;
        
        // If we don't have the cursor for a non-sequential jump, fetch intermediate cursors
        if (!cursor && page > 1) {
          
          try {
            const intermediateCursors = await fetchOrderPageCursors(userId, page, pageSize, status);
            
            // Merge the intermediate cursors into our existing cursors
            intermediateCursors.forEach((value, key) => {
              pageCursorsRef.current.set(key, value);
            });
            
            // Now try to get the cursor again
            cursor = pageCursorsRef.current.get(page - 1) || null;
          } catch (err) {
            // Fallback to sequential fetch
            
            // Fallback: sequentially fetch pages to build up cursors
            let currentCursor: QueryDocumentSnapshot<DocumentData> | null = null;
            
            for (let p = 1; p < page; p++) {
              if (!pageCursorsRef.current.has(p)) {
                const result = await fetchOrdersPaginated(userId, pageSize, status, currentCursor);
                if (result.lastDoc) {
                  pageCursorsRef.current.set(p, result.lastDoc);
                  currentCursor = result.lastDoc;
                }
              } else {
                currentCursor = pageCursorsRef.current.get(p) || null;
              }
            }
            
            cursor = currentCursor;
          }
        }
      }
      
      const result: PaginatedOrdersResult = await fetchOrdersPaginated(
        userId,
        pageSize,
        status,
        cursor
      );
      
      setOrders(result.orders);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
      // Store the cursor for the next page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page, result.lastDoc);
      }
      
    } catch (err) {
      setError('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, userId, pageSize, status]);

  // Fetch next page (append to current orders)
  const fetchNextPage = useCallback(async () => {
    if (!hasMore || loading) return;
    
    try {
      setLoading(true);
      
      const cursor = pageCursorsRef.current.get(currentPage);
      const result = await fetchOrdersPaginated(userId, pageSize, status, cursor);
      
      setOrders(prev => [...prev, ...result.orders]);
      setHasMore(result.hasMore);
      setCurrentPage(prev => prev + 1);
      
      if (result.lastDoc) {
        pageCursorsRef.current.set(currentPage + 1, result.lastDoc);
      }
      
    } catch (err) {
      setError('Failed to load more orders');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, currentPage, userId, pageSize, status]);

  // Fetch specific page
  const fetchPage = useCallback(async (page: number) => {
    if (page < 1 || page > totalPages) return;
    await loadPage(page);
  }, [loadPage, totalPages]);

  // Reset pagination
  const reset = useCallback(() => {
    setOrders([]);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    setHasMore(false);
    setError(null);
  }, []);

  // Initial load and when dependencies change
  useEffect(() => {
    if (!enabled || !userId) {
      setLoading(false);
      return;
    }

    const initialize = async () => {
      // Reset state when status changes
      setOrders([]);
      setCurrentPage(1);
      pageCursorsRef.current.clear();
      setHasMore(false);
      setError(null);
      
      // Load total count
      try {
        const count = await getOrderCount(userId, status);
        setTotalCount(count);
      } catch (err) {
        setError('Failed to load order count');
        setTotalCount(0);
      }
      
      // Load first page
      await loadPage(1);
    };

    initialize();
  }, [userId, status, enabled, loadPage]);

  return {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    hasMore,
    fetchNextPage,
    fetchPage,
    reset
  };
}; 